import { useQuery } from '@apollo/client';
import { Save } from '@mui/icons-material';
import { Box, Button, type DialogProps } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useAppContext } from '~/contexts';
import type { Playlist, Zone } from '~/generated/graphql';
import { PlaylistSelectDialogDocument } from './PlaylistSelectDialog.generated';
import { PlaylistSelectTable, useColumns } from './PlaylistSelectTable';

/* GraphQL */ `#graphql
query PlaylistSelectDialog($networkId: Int!) {
  network(id: $networkId) {
    id
    allPlaylists {
      ...PlaylistAdd__Playlist
    }
  }
}
fragment PlaylistAdd__Playlist on Playlist {
  id
  name
  playlistItems {
    id
  }
  reusable
  runtime {
    ...Interval
  }
  showCount
}
`;

export interface PlaylistSelectDialogProps extends Omit<DialogProps, 'onChange'> {
  onClose: () => void;
  onChange: (selectedId: number | null) => void;
  playlist?: Pick<Playlist, 'id' | 'name' | 'reusable'>;
  zoneFilter: Pick<Zone, 'height' | 'name' | 'width'>;
}

export const PlaylistSelectDialog = ({
  onClose,
  onChange,
  playlist,
  zoneFilter,
  ...props
}: PlaylistSelectDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [selectedPlaylistId, setSelectedPlaylistId] = useState<number | null>(playlist?.id ?? null);

  const { data, loading } = useQuery(PlaylistSelectDialogDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const clearState = () => {
    setSelectedPlaylistId(null);
  };

  const columns = useColumns();

  return (
    <Dialog
      TransitionProps={{ onExited: clearState }}
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      {...props}
    >
      <DialogTitle onClose={onClose}>
        {t('pages.shows.detail.dialogs.playlist_select.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentTitle>
          <Trans
            i18nKey="pages.shows.detail.dialogs.playlist_select.description"
            values={{ zoneName: zoneFilter.name }}
            components={{ 0: <strong /> }}
          />
        </DialogContentTitle>
        <Box>
          <PlaylistSelectTable
            columns={columns}
            loading={loading}
            onCheck={(id) => setSelectedPlaylistId(() => id)}
            rows={data?.network?.allPlaylists ?? []}
            selectedItemId={selectedPlaylistId}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<Save />}
          disabled={!selectedPlaylistId || playlist?.id === selectedPlaylistId}
          onClick={() => {
            onClose();
            onChange(selectedPlaylistId);
          }}
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
